import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { BackgroundSettings, PresetBackgroundId } from '../types';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm'];

// Theme colors for preset backgrounds (from themeService)
interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  border: string;
}

// Preset background configurations
export const presetBackgrounds: Record<PresetBackgroundId, { 
  name: string; 
  url: string; 
  thumbnail?: string;
  requiresSubscription?: boolean;
  tier?: 'trial' | 'e_rank' | 's_rank';
  themeColors?: ThemeColors; // Optional color scheme to apply with background
}> = {
  theme_jeju: {
    name: 'Shadow Throne',
    url: '/themes/6.png',
    requiresSubscription: true,
    tier: 's_rank',
    themeColors: {
      primary: '#8B5CF6',
      secondary: '#DC2626',
      accent: '#F97316',
      background: 'rgba(51, 65, 85, 0.5)',
      surface: 'rgba(71, 85, 105, 0.6)',
      text: '#FFFFFF',
      textSecondary: '#A78BFA',
      border: '#64748B',
    },
  },
  theme_blue: {
    name: 'Void Dominion',
    url: '/themes/1.png',
    requiresSubscription: false,
    themeColors: {
      primary: '#DC2626',
      secondary: '#991B1B',
      accent: '#F87171',
      background: 'rgba(51, 65, 85, 0.5)',
      surface: 'rgba(71, 85, 105, 0.6)',
      text: '#FFFFFF',
      textSecondary: '#FCA5A5',
      border: '#64748B',
    },
  },
  theme_shadow: {
    name: 'Shadow Legion',
    url: '/themes/5.png',
    requiresSubscription: true,
    tier: 's_rank',
    themeColors: {
      primary: '#A78BFA',
      secondary: '#8B5CF6',
      accent: '#C4B5FD',
      background: 'rgba(51, 65, 85, 0.5)',
      surface: 'rgba(71, 85, 105, 0.6)',
      text: '#FFFFFF',
      textSecondary: '#A78BFA',
      border: '#64748B',
    },
  },
  theme_red: {
    name: 'Crimson Dominion',
    url: '/themes/4.png',
    requiresSubscription: true,
    tier: 's_rank',
    themeColors: {
      primary: '#3B82F6',
      secondary: '#1E40AF',
      accent: '#60A5FA',
      background: 'rgba(51, 65, 85, 0.5)',
      surface: 'rgba(71, 85, 105, 0.6)',
      text: '#FFFFFF',
      textSecondary: '#94A3B8',
      border: '#64748B',
    },
  },
  theme_ice: {
    name: 'Frost Dominion',
    url: '/themes/2.png',
    requiresSubscription: true,
    tier: 's_rank',
    themeColors: {
      primary: '#06B6D4',
      secondary: '#0891B2',
      accent: '#67E8F9',
      background: 'rgba(51, 65, 85, 0.5)',
      surface: 'rgba(71, 85, 105, 0.6)',
      text: '#FFFFFF',
      textSecondary: '#A5F3FC',
      border: '#64748B',
    },
  },
  theme_gold: {
    name: 'Golden Warrior',
    url: '/themes/3.png',
    requiresSubscription: true,
    tier: 's_rank',
    themeColors: {
      primary: '#F59E0B',
      secondary: '#D97706',
      accent: '#FCD34D',
      background: 'rgba(51, 65, 85, 0.5)',
      surface: 'rgba(71, 85, 105, 0.6)',
      text: '#FFFFFF',
      textSecondary: '#FDE68A',
      border: '#64748B',
    },
  },
  abstract_purple: {
    name: 'Arcane Dominion',
    url: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
    requiresSubscription: false,
    themeColors: {
      primary: '#8B5CF6',
      secondary: '#7C3AED',
      accent: '#A78BFA',
      background: 'rgba(51, 65, 85, 0.5)',
      surface: 'rgba(71, 85, 105, 0.6)',
      text: '#FFFFFF',
      textSecondary: '#E9D5FF',
      border: '#64748B',
    },
  },
  abstract_blue: {
    name: 'Abstract Blue',
    url: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 50%, #43e97b 100%)',
    requiresSubscription: false,
    themeColors: {
      primary: '#06B6D4',
      secondary: '#00f2fe',
      accent: '#43e97b',
      background: 'rgba(51, 65, 85, 0.5)',
      surface: 'rgba(71, 85, 105, 0.6)',
      text: '#FFFFFF',
      textSecondary: '#A5F3FC',
      border: '#64748B',
    },
  },
  abstract_pink: {
    name: 'Abstract Pink',
    url: 'linear-gradient(135deg, #ff6b9d 0%, #c06c84 50%, #fbb034 100%)',
    requiresSubscription: false,
    themeColors: {
      primary: '#EC4899',
      secondary: '#c06c84',
      accent: '#fbb034',
      background: 'rgba(51, 65, 85, 0.5)',
      surface: 'rgba(71, 85, 105, 0.6)',
      text: '#FFFFFF',
      textSecondary: '#FBCFE8',
      border: '#64748B',
    },
  },
  abstract_orange: {
    name: 'Abstract Orange',
    url: 'linear-gradient(135deg, #ff9a56 0%, #ff6a88 50%, #ffeaa7 100%)',
    requiresSubscription: false,
    themeColors: {
      primary: '#F97316',
      secondary: '#ff6a88',
      accent: '#ffeaa7',
      background: 'rgba(51, 65, 85, 0.5)',
      surface: 'rgba(71, 85, 105, 0.6)',
      text: '#FFFFFF',
      textSecondary: '#FED7AA',
      border: '#64748B',
    },
  },
  neon_grid: {
    name: 'Neon Grid',
    url: 'linear-gradient(135deg, #fa709a 0%, #fee140 50%, #30cfd0 100%)',
    requiresSubscription: false,
    themeColors: {
      primary: '#EC4899',
      secondary: '#fee140',
      accent: '#30cfd0',
      background: 'rgba(51, 65, 85, 0.5)',
      surface: 'rgba(71, 85, 105, 0.6)',
      text: '#FFFFFF',
      textSecondary: '#FDE68A',
      border: '#64748B',
    },
  },
  default_dark: {
    name: 'Default Dark',
    url: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    requiresSubscription: false,
    themeColors: {
      primary: '#3B82F6',
      secondary: '#8B5CF6',
      accent: '#60A5FA',
      background: 'rgba(51, 65, 85, 0.5)',
      surface: 'rgba(71, 85, 105, 0.6)',
      text: '#FFFFFF',
      textSecondary: '#94A3B8',
      border: '#64748B',
    },
  },
};

/**
 * Get default background settings
 */
export const getDefaultBackgroundSettings = (): BackgroundSettings => ({
  type: 'preset',
  presetId: 'default_dark',
  overlayOpacity: 30,
  blurIntensity: 0,
  particleEffect: 'none',
  particleIntensity: 50,
  parallaxEnabled: false,
  reducedQuality: false,
  imageCollection: [],
  videoCollection: [],
});

/**
 * Clean up stale background data based on current type
 * Removes URLs from deleted backgrounds to prevent them from showing
 */
export const cleanBackgroundSettings = (settings: BackgroundSettings): BackgroundSettings => {
  const cleaned = { ...settings };

  // Based on the current type, remove incompatible/stale URLs
  switch (settings.type) {
    case 'preset':
      // Remove custom uploads when using presets
      delete cleaned.customImageUrl;
      delete cleaned.customImageName;
      delete cleaned.videoUrl;
      delete cleaned.videoName;
      delete cleaned.gradient;
      delete cleaned.solidColor;
      break;

    case 'custom_image':
      // Remove everything except custom image
      delete cleaned.presetId;
      delete cleaned.videoUrl;
      delete cleaned.videoName;
      delete cleaned.gradient;
      delete cleaned.solidColor;
      break;

    case 'gradient':
      // Remove everything except gradient
      delete cleaned.presetId;
      delete cleaned.customImageUrl;
      delete cleaned.customImageName;
      delete cleaned.videoUrl;
      delete cleaned.videoName;
      delete cleaned.solidColor;
      break;

    case 'video':
      // Remove everything except video
      delete cleaned.presetId;
      delete cleaned.customImageUrl;
      delete cleaned.customImageName;
      delete cleaned.gradient;
      delete cleaned.solidColor;
      break;

    case 'solid':
      // Remove everything except solid color
      delete cleaned.presetId;
      delete cleaned.customImageUrl;
      delete cleaned.customImageName;
      delete cleaned.videoUrl;
      delete cleaned.videoName;
      delete cleaned.gradient;
      break;
  }

  return cleaned;
};

/**
 * Optimize image before upload (resize and compress)
 */
const optimizeImage = (file: File, maxWidth: number = 1920, maxHeight: number = 1080): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }

    img.onload = () => {
      let { width, height } = img;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.floor(width * ratio);
        height = Math.floor(height * ratio);
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        'image/jpeg',
        0.85 // 85% quality
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Upload custom background image to Firebase Storage
 */
export const uploadBackgroundImage = async (
  userId: string,
  file: File
): Promise<{ url: string; fileName: string }> => {
  // Validate file type
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error('Invalid file type. Please upload a JPEG, PNG, or WebP image.');
  }

  // Validate file size
  if (file.size > MAX_FILE_SIZE) {
    throw new Error('File size exceeds 10MB limit.');
  }

  try {
    // Optimize image
    const optimizedBlob = await optimizeImage(file);
    
    // Create storage reference
    const storage = getStorage();
    const fileName = `background_${Date.now()}.jpg`;
    const storageRef = ref(storage, `backgrounds/${userId}/${fileName}`);

    // Upload
    await uploadBytes(storageRef, optimizedBlob);
    
    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);

    return { url: downloadURL, fileName };
  } catch (error) {
    console.error('Error uploading background:', error);
    throw new Error('Failed to upload background image. Please try again.');
  }
};

/**
 * Upload custom background video to Firebase Storage
 */
export const uploadBackgroundVideo = async (
  userId: string,
  file: File
): Promise<{ url: string; fileName: string }> => {
  // Validate file type
  if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
    throw new Error('Invalid file type. Please upload an MP4 or WebM video.');
  }

  // Validate file size (larger limit for videos)
  const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB
  if (file.size > MAX_VIDEO_SIZE) {
    throw new Error('Video size exceeds 50MB limit.');
  }

  try {
    const storage = getStorage();
    const fileName = `video_${Date.now()}.${file.type === 'video/mp4' ? 'mp4' : 'webm'}`;
    const storageRef = ref(storage, `backgrounds/${userId}/${fileName}`);

    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    return { url: downloadURL, fileName };
  } catch (error) {
    console.error('Error uploading video:', error);
    throw new Error('Failed to upload background video. Please try again.');
  }
};

/**
 * Delete custom background from Firebase Storage
 */
export const deleteBackgroundFile = async (userId: string, fileName: string): Promise<void> => {
  try {
    const storage = getStorage();
    const storageRef = ref(storage, `backgrounds/${userId}/${fileName}`);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting background:', error);
    // Don't throw - file might already be deleted
  }
};

/**
 * Save background settings to Firestore
 * Removes undefined fields as Firestore doesn't accept them
 */
export const saveBackgroundSettings = async (
  userId: string,
  settings: BackgroundSettings
): Promise<void> => {
  try {
    // Clean settings object - remove undefined fields for Firestore
    const cleanSettings: any = {};
    Object.keys(settings).forEach((key) => {
      const value = (settings as any)[key];
      if (value !== undefined) {
        cleanSettings[key] = value;
      }
    });

    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      backgroundSettings: cleanSettings,
    });
  } catch (error) {
    console.error('Error saving background settings:', error);
    throw new Error('Failed to save background settings.');
  }
};

/**
 * Generate CSS for background based on settings
 */
export const generateBackgroundCSS = (settings: BackgroundSettings): React.CSSProperties => {
  const styles: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    zIndex: -2,
  };

  // IMPORTANT: Only apply styles that match the current type
  // This prevents old deleted images/videos from showing when type changes
  switch (settings.type) {
    case 'preset':
      // Safety check: fallback to default if preset doesn't exist (migration from old presets)
      const presetId = settings.presetId && presetBackgrounds[settings.presetId] 
        ? settings.presetId 
        : 'default_dark';
      const preset = presetBackgrounds[presetId];
      
      if (preset && preset.url.startsWith('linear-gradient')) {
        styles.background = preset.url;
      } else if (preset) {
        styles.backgroundImage = `url(${preset.url})`;
        styles.backgroundSize = 'cover';
        styles.backgroundPosition = 'center';
        styles.backgroundRepeat = 'no-repeat';
      }
      break;

    case 'custom_image':
      // Only show custom image if type is explicitly 'custom_image' AND URL exists
      if (settings.customImageUrl) {
        styles.backgroundImage = `url(${settings.customImageUrl})`;
        styles.backgroundSize = 'cover';
        styles.backgroundPosition = 'center';
        styles.backgroundRepeat = 'no-repeat';
      } else {
        // Fallback if no custom image URL - use default dark gradient
        styles.background = 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)';
      }
      break;

    case 'gradient':
      if (settings.gradient && settings.gradient.colors.length > 0) {
        const { colors, angle } = settings.gradient;
        styles.background = `linear-gradient(${angle}deg, ${colors.join(', ')})`;
      } else {
        styles.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      }
      break;

    case 'solid':
      if (settings.solidColor) {
        styles.backgroundColor = settings.solidColor;
      } else {
        styles.backgroundColor = '#1a1a2e';
      }
      break;

    case 'video':
      // Video backgrounds are handled separately by BackgroundRenderer
      // Only show black while video loads, don't show any old images
      styles.backgroundColor = '#000';
      break;

    default:
      // Fallback for unknown types
      styles.background = 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)';
  }

  // Apply blur
  if (settings.blurIntensity > 0) {
    styles.filter = `blur(${settings.blurIntensity}px)`;
  }

  return styles;
};

/**
 * Validate gradient colors
 */
export const validateGradientColor = (color: string): boolean => {
  // Simple hex color validation
  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  return hexRegex.test(color);
};

/**
 * Apply theme colors to the document root
 */
export const applyThemeColors = (colors: ThemeColors) => {
  const root = document.documentElement;
  root.style.setProperty('--color-primary', colors.primary);
  root.style.setProperty('--color-secondary', colors.secondary);
  root.style.setProperty('--color-accent', colors.accent);
  root.style.setProperty('--color-background', colors.background);
  root.style.setProperty('--color-surface', colors.surface);
  root.style.setProperty('--color-text', colors.text);
  root.style.setProperty('--color-text-secondary', colors.textSecondary);
  root.style.setProperty('--color-border', colors.border);
};

/**
 * Get theme colors for a preset (if available)
 */
export const getPresetThemeColors = (presetId: PresetBackgroundId): ThemeColors | null => {
  const preset = presetBackgrounds[presetId];
  return preset?.themeColors || null;
};

/**
 * Convert hex to RGB
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Darken a color by a percentage
 */
const darkenColor = (hex: string, percent: number): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  const r = Math.max(0, Math.floor(rgb.r * (1 - percent)));
  const g = Math.max(0, Math.floor(rgb.g * (1 - percent)));
  const b = Math.max(0, Math.floor(rgb.b * (1 - percent)));
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Lighten a color by a percentage
 */
const lightenColor = (hex: string, percent: number): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  const r = Math.min(255, Math.floor(rgb.r + (255 - rgb.r) * percent));
  const g = Math.min(255, Math.floor(rgb.g + (255 - rgb.g) * percent));
  const b = Math.min(255, Math.floor(rgb.b + (255 - rgb.b) * percent));
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Generate complete theme colors from gradient
 * Creates harmonious, readable color scheme
 */
export const generateThemeFromGradient = (gradientColors: string[]): ThemeColors => {
  // Use first gradient color as primary
  const primary = gradientColors[0];
  // Use second color as secondary (or darken primary if only one color)
  const secondary = gradientColors[1] || darkenColor(primary, 0.2);
  // Use third color as accent (or lighten primary)
  const accent = gradientColors[2] || lightenColor(primary, 0.3);
  
  // Create dark, semi-transparent backgrounds for readability
  const primaryRgb = hexToRgb(primary);
  const background = primaryRgb 
    ? `rgba(${Math.floor(primaryRgb.r * 0.1)}, ${Math.floor(primaryRgb.g * 0.1)}, ${Math.floor(primaryRgb.b * 0.15)}, 0.85)`
    : 'rgba(15, 23, 42, 0.85)';
  
  const surface = primaryRgb
    ? `rgba(${Math.floor(primaryRgb.r * 0.15)}, ${Math.floor(primaryRgb.g * 0.15)}, ${Math.floor(primaryRgb.b * 0.2)}, 0.9)`
    : 'rgba(30, 41, 59, 0.9)';
  
  // Text is always white for dark backgrounds
  const text = '#FFFFFF';
  // Text secondary uses a lightened version of the accent
  const textSecondary = lightenColor(accent, 0.3);
  
  // Border uses primary color
  const border = primary;
  
  return {
    primary,
    secondary,
    accent,
    background,
    surface,
    text,
    textSecondary,
    border,
  };
};

/**
 * Generate random gradient with complete theme colors
 * All color combinations are tested for harmony and readability
 */
export const generateRandomGradient = (): { 
  colors: string[]; 
  angle: number;
  themeColors: ThemeColors;
} => {
  const presets = [
    // Purple Dream - Mystical and royal
    { 
      colors: ['#667eea', '#764ba2'], 
      angle: 135,
      name: 'Purple Dream'
    },
    // Pink Sunset - Warm and vibrant
    { 
      colors: ['#f093fb', '#f5576c'], 
      angle: 45,
      name: 'Pink Sunset'
    },
    // Ocean Blue - Cool and calming
    { 
      colors: ['#4facfe', '#00f2fe'], 
      angle: 90,
      name: 'Ocean Blue'
    },
    // Emerald Garden - Fresh and natural
    { 
      colors: ['#43e97b', '#38f9d7'], 
      angle: 180,
      name: 'Emerald Garden'
    },
    // Sunrise - Energetic and warm
    { 
      colors: ['#fa709a', '#fee140'], 
      angle: 135,
      name: 'Sunrise'
    },
    // Deep Space - Mysterious and deep
    { 
      colors: ['#30cfd0', '#330867'], 
      angle: 45,
      name: 'Deep Space'
    },
    // Pastel Dream - Soft and gentle
    { 
      colors: ['#a8edea', '#fed6e3'], 
      angle: 120,
      name: 'Pastel Dream'
    },
    // Rose Quartz - Elegant and sophisticated
    { 
      colors: ['#ff9a9e', '#fecfef'], 
      angle: 135,
      name: 'Rose Quartz'
    },
    // Northern Lights - Magical and ethereal
    { 
      colors: ['#00c6ff', '#0072ff', '#a8ff78'], 
      angle: 90,
      name: 'Northern Lights'
    },
    // Fire & Ice - Contrasting and bold
    { 
      colors: ['#ff6b6b', '#4ecdc4'], 
      angle: 45,
      name: 'Fire & Ice'
    },
    // Shadow Monarch - Purple power
    { 
      colors: ['#8B5CF6', '#6366F1', '#A78BFA'], 
      angle: 135,
      name: 'Shadow Monarch'
    },
    // Golden Hour - Warm and luxurious
    { 
      colors: ['#F59E0B', '#D97706', '#FCD34D'], 
      angle: 180,
      name: 'Golden Hour'
    },
  ];

  const selected = presets[Math.floor(Math.random() * presets.length)];
  
  return {
    colors: selected.colors,
    angle: selected.angle,
    themeColors: generateThemeFromGradient(selected.colors),
  };
};

